import { alpha, styled, type CSSObject, type Theme } from "@mui/material";

// Shared focus glow for every DS form field, so they look visually identical.
// A soft halo fading over ~6px, used as box-shadow (only around the border-box,
// never under content) so it stays subtle and consistent across all fields.
const focusGlow = (color: string): string => `0 0 6px 0 ${alpha(color, 0.25)}`;

const Container = styled("div")<{ $fullWidth?: boolean; $toolbar?: boolean }>(
  ({ $fullWidth, $toolbar, theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: $toolbar ? 0 : theme.spacing(1),
    ...(!$fullWidth &&
      !$toolbar && {
        minWidth: "280px",
        maxWidth: "560px",
      }),
  }),
);

const Label = styled("label")(({ theme }) => ({
  color: theme.palette.cf.cfDarkerGrey,
  fontSize: "14px",
  lineHeight: "14px",
  textAlign: "left",
}));

type FieldSizing = "fixed" | "multiline" | "auto";

const INPUT_WRAPPER_SIZING: Record<FieldSizing, (theme: Theme) => CSSObject> = {
  fixed: (theme) => ({ height: "44px", padding: `0 ${theme.spacing(1.5)}` }),
  multiline: (theme) => ({ padding: theme.spacing(1.25, 1.5) }),
  auto: (theme) => ({ minHeight: "44px", padding: theme.spacing(0.75, 1.5) }),
};

const InputWrapper = styled("div")<{
  $error?: boolean;
  $disabled?: boolean;
  $sizing?: FieldSizing;
}>(({ $disabled, $error, $sizing = "fixed", theme }) => {
  const focusColor = $error
    ? theme.palette.error.main
    : theme.palette.cf.cfPrimary;
  const borderColor = $error
    ? theme.palette.error.main
    : theme.palette.cf.cfGrey;
  return {
    display: "flex",
    alignItems: $sizing === "multiline" ? "stretch" : "center",
    ...INPUT_WRAPPER_SIZING[$sizing](theme),
    borderRadius: theme.spacing(1),
    gap: theme.spacing(1),
    backgroundColor: $disabled ? theme.palette.cf.cfLightGrey : "transparent",
    boxSizing: "border-box",
    boxShadow: `inset 0 0 0 1px ${borderColor}`,
    ...(!$disabled && {
      "&:hover": {
        boxShadow: `inset 0 0 0 1px ${focusColor}`,
      },
    }),
    "&:focus-within": {
      boxShadow: `inset 0 0 0 1px ${focusColor}, ${focusGlow(focusColor)}`,
    },
  };
});

const fieldTextStyles = (
  $error: boolean | undefined,
  theme: Theme,
): CSSObject => ({
  flex: 1,
  alignSelf: "stretch",
  minWidth: 0,
  border: "none",
  outline: "none",
  background: "transparent",
  fontSize: "14px",
  fontWeight: 500,
  color: theme.palette.cf.cfBlack,
  fontFamily: "inherit",
  "&::placeholder": {
    color: theme.palette.cf.cfDarkerGrey,
    fontWeight: 400,
  },
  "&:focus": {
    caretColor: $error ? theme.palette.error.main : theme.palette.cf.cfPrimary,
    "&::placeholder": { color: "transparent" },
  },
  "&:disabled": {
    color: theme.palette.cf.cfBlack,
    cursor: "not-allowed",
  },
});

const StyledInput = styled("input")<{ $error?: boolean }>(
  ({ $error, theme }) => ({
    ...fieldTextStyles($error, theme),
    "&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active":
      {
        WebkitTextFillColor: theme.palette.cf.cfBlack,
        transition: "background-color 600000s 0s, color 600000s 0s",
      },
  }),
);

const StyledTextArea = styled("textarea")<{ $error?: boolean }>(
  ({ $error, theme }) => ({
    ...fieldTextStyles($error, theme),
    padding: 0,
    margin: 0,
    lineHeight: "20px",
    resize: "vertical",
    overflowY: "auto",
    ...thinScrollbar(theme),
  }),
);

const IconSlot = styled("span")<{ $disabled?: boolean }>(
  ({ $disabled, theme }) => ({
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
    color: $disabled ? theme.palette.cf.cfDarkGrey : theme.palette.cf.cfPrimary,
    fontSize: "20px",
  }),
);

const ErrorMessage = styled("span")(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: "12px",
  lineHeight: "12px",
}));

const ChevronSlot = styled(IconSlot, {
  shouldForwardProp: (prop) => prop !== "$isOpen" && prop !== "$disabled",
})<{
  $isOpen?: boolean;
  $disabled?: boolean;
}>(({ $disabled, $isOpen }) => ({
  transition: "transform 0.2s ease",
  transform: $isOpen ? "rotate(180deg)" : "rotate(0deg)",
  cursor: $disabled ? "not-allowed" : "pointer",
}));

const dropdownSurface = (theme: Theme) => ({
  "@keyframes dropdownOpen": {
    from: {
      opacity: 0,
      transform: "translateY(-4px)",
    },
    to: {
      opacity: 1,
      transform: "translateY(0)",
    },
  },
  backgroundColor: theme.palette.common.white,
  borderRadius: theme.spacing(1),
  boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.1)",
  overflow: "hidden",
  animation: "dropdownOpen 0.15s ease-out",
});

// Marked so useDropdown can tell a click inside *some* portalled dropdown from a
// genuine outside click. A dropdown nested in another one (the preset SelectBox
// inside DateRangePicker) portals to document.body, so it is not a DOM
// descendant of the outer dropdown and would otherwise read as an outside click
// and close its own parent before the selection lands (DEV-2117).
const PORTAL_DROPDOWN_ATTRIBUTE = "data-portal-dropdown";

const PORTAL_DROPDOWN_Z_INDEX_VAR = "--portal-dropdown-z-index";

const PortalDropdownContainer = styled("div")(({ theme }) => ({
  ...dropdownSurface(theme),
  position: "fixed",
  top: 0,
  left: 0,
  display: "flex",
  flexDirection: "column",
  visibility: "hidden",
  zIndex: `var(${PORTAL_DROPDOWN_Z_INDEX_VAR}, ${theme.zIndex.tooltip})`,
}));

const thinScrollbar = (theme: Theme) => ({
  "&::-webkit-scrollbar": {
    width: "4px",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: theme.palette.cf.cfGrey,
    borderRadius: "4px",
  },
});

const OptionsList = styled("ul")(({ theme }) => ({
  listStyle: "none",
  margin: 0,
  padding: 0,
  maxHeight: "200px",
  overflowY: "auto",
  overflowX: "hidden",
  overscrollBehavior: "contain",
  ...thinScrollbar(theme),
}));

const getOptionBackground = (
  $isSelected: boolean,
  $isFocused: boolean,
  theme: Theme,
): string => {
  if ($isSelected) return theme.palette.cf.cfLightGreen;
  if ($isFocused) return theme.palette.cf.cfLightestGrey;
  return "transparent";
};

const OptionItem = styled("li")<{
  $isFocused?: boolean;
  $isSelected?: boolean;
}>(({ $isFocused, $isSelected, theme }) => ({
  padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
  fontSize: "14px",
  fontWeight: $isSelected || $isFocused ? 500 : 400,
  color: $isSelected ? theme.palette.cf.cfPrimary : theme.palette.cf.cfBlack,
  backgroundColor: getOptionBackground(!!$isSelected, !!$isFocused, theme),
  cursor: "pointer",
}));

const SelectWrapper = styled("div")(() => ({
  position: "relative",
}));

const Trigger = styled("button")<{
  $disabled?: boolean;
  $error?: boolean;
  $isOpen?: boolean;
  $toolbar?: boolean;
}>(({ $disabled, $error, $isOpen, $toolbar, theme }) => {
  const focusColor = $error
    ? theme.palette.error.main
    : theme.palette.cf.cfPrimary;
  // A toolbar control stands among outlined buttons, so it wears their resting border rather than the grey a
  // form field wears until it is touched.
  const restingBorder = $toolbar
    ? theme.palette.cf.cfPrimary
    : theme.palette.cf.cfGrey;
  const borderColor = $error ? theme.palette.error.main : restingBorder;
  const focusBoxShadow = `inset 0 0 0 1px ${focusColor}, ${focusGlow(focusColor)}`;
  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    lineHeight: 1,
    width: $toolbar ? "max-content" : "100%",
    height: "44px",
    border: 0,
    borderRadius: theme.spacing(1),
    padding: `0 ${theme.spacing(1.5)}`,
    backgroundColor: $disabled ? theme.palette.cf.cfLightGrey : "transparent",
    cursor: $disabled ? "not-allowed" : "pointer",
    fontFamily: "inherit",
    boxSizing: "border-box",
    boxShadow: `inset 0 0 0 1px ${borderColor}`,
    outline: "none",
    ...(!$disabled &&
      !$isOpen && {
        "&:hover": {
          boxShadow: `inset 0 0 0 1px ${focusColor}`,
        },
      }),
    "&:focus-within": {
      boxShadow: focusBoxShadow,
    },
    ...(!$disabled && {
      "&:focus-visible": {
        boxShadow: focusBoxShadow,
      },
    }),
    ...($isOpen && {
      boxShadow: focusBoxShadow,
    }),
  };
});

const TriggerText = styled("span")<{ $hasValue?: boolean }>(
  ({ $hasValue, theme }) => ({
    fontSize: "14px",
    fontWeight: $hasValue ? 500 : 400,
    lineHeight: 1.3,
    color: $hasValue ? theme.palette.cf.cfBlack : theme.palette.cf.cfDarkerGrey,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }),
);

export {
  ChevronSlot,
  Container,
  ErrorMessage,
  focusGlow,
  IconSlot,
  InputWrapper,
  Label,
  OptionItem,
  OptionsList,
  PORTAL_DROPDOWN_ATTRIBUTE,
  PORTAL_DROPDOWN_Z_INDEX_VAR,
  PortalDropdownContainer,
  SelectWrapper,
  StyledInput,
  StyledTextArea,
  thinScrollbar,
  Trigger,
  TriggerText,
};
